import { logger } from '../core/logger';

/**
 * Common selectors for email input fields.
 */
const EMAIL_SELECTORS = [
    'input[type="email"]',
    'input[name="email"]',
    'input[id="email"]',
    'input[name*="email" i]',
    'input[id*="email" i]',
    'input[placeholder*="email" i]',
];

const filledFields = new WeakSet<HTMLInputElement>();
let autoFillInitialized = false;

function fillEmailField(field: HTMLInputElement, email: string): boolean {
    if (filledFields.has(field) || field.value) {
        return false;
    }

    // Use native setter to bypass React's internal value tracker.
    // React 16+ wraps the value property with a tracker that silently updates
    // when .value is set directly, causing dispatched input/change events to be ignored.
    const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
    )?.set;

    if (nativeSetter) {
        nativeSetter.call(field, email);
    } else {
        field.value = email;
    }

    filledFields.add(field);

    // Dispatch events for React/Vue/Angular form state updates
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));

    logger.debug('autoFillEmailFromUrl: Filled email field');
    return true;
}

function fillExistingEmailFields(email: string): number {
    let filledCount = 0;
    for (const selector of EMAIL_SELECTORS) {
        document.querySelectorAll<HTMLInputElement>(selector).forEach((field) => {
            if (fillEmailField(field, email)) {
                filledCount++;
            }
        });
    }
    return filledCount;
}

/**
 * Auto-fill email fields on the page from URL parameter.
 *
 * Called when site config is loaded to pre-fill email fields on booking pages
 * with the value passed via the `?email=` query parameter.
 *
 * Limitations:
 * - Does not work for iframe-based forms (Typeform, Calendly, etc.)
 *   For these, the form owner should configure hidden fields to read URL params.
 * - Only works for native HTML forms rendered directly on the page.
 */
export function autoFillEmailFromUrl(): void {
    if (typeof window === 'undefined') return;
    if (autoFillInitialized) return;

    const params = new URLSearchParams(window.location.search);
    const email = params.get('email');

    if (!email || !email.includes('@')) return;

    autoFillInitialized = true;
    logger.info('autoFillEmailFromUrl: Found email in URL, attempting auto-fill');

    // Fill existing fields immediately
    const count = fillExistingEmailFields(email);
    if (count > 0) {
        logger.info(`autoFillEmailFromUrl: Filled ${count} field(s)`);
    }

    // Watch for dynamically added fields (SPAs)
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node instanceof Element) {
                    for (const selector of EMAIL_SELECTORS) {
                        node.querySelectorAll<HTMLInputElement>(selector).forEach((field) => {
                            fillEmailField(field, email);
                        });
                    }
                }
            }
        }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Stop after 10 seconds
    setTimeout(() => observer.disconnect(), 10000);
}
